/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { Redirect } from "react-router-dom";
import { GuidePage } from "api/CmsApi";
import { AppPath } from "components/AppPath";
import { GuidePageH1, imageWidth } from "./GuidePageBlocks";
import styled from "styled-components";
import { AppHeaderHeight } from "components/shared/AppHeader";
import { devices } from "components/shared/Styles";
import { Link } from "react-router-dom";

const GuidePagePreviewContainer = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-decoration: inherit;
`;

const PreviewTitle = styled.h2`
  color: var(--spectrum-global-color-static-gray-900);
  font-size: 22px;
  font-weight: 900;
  line-height: 24.2px;
  margin-top: 8px;
`;

const PreviewImage = styled.img`
  border-radius: 24px;
  display: block;
  object-fit: cover;
  min-height: 278px;
`;

const PreviewSubtitle = styled.h3`
  color: var(--spectrum-global-color-static-gray-900);
  font-size: 16px;
  font-weight: 400;
  line-height: 20.8px;
`;

const GuidePagePreview: React.FC<{ guidePage: GuidePage }> = ({
  guidePage,
}) => {
  return (
    <GuidePagePreviewContainer to={`guides/${guidePage.attributes.slug}`}>
      <PreviewImage src={guidePage.attributes.Hero.data.attributes.url} />
      <PreviewTitle>{guidePage.attributes.Title}</PreviewTitle>
      <PreviewSubtitle>{guidePage.attributes.Subtitle}</PreviewSubtitle>
    </GuidePagePreviewContainer>
  );
};

const GuidePagesPreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin-top: 56px;
  gap: 56px;

  > * {
    width: 388px;
  }

  ${devices.medium} {
    > * {
      width: 100%;
    }
  }

  ${devices.small} {
    flex-direction: column;
    align-items: center;
    gap: 40px;

    > * {
      width: 100%;
    }
  }
`;

const IndexPageTitle = styled(GuidePageH1)`
  background: linear-gradient(133.13deg, #5266ff 5.68%, #b871ff 96.33%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-top: 64px;
  margin-bottom: 56px;

  ${devices.small} {
    margin-top: 32px;
    margin-bottom: 40px;
  }
`;

export const GuidePageIndex: React.FC<{ guidePages: GuidePage[] }> = ({
  guidePages,
}) => {
  if (guidePages.length === 0) {
    return <Redirect to={AppPath.ROOT} />;
  }

  const [firstGuidePage, ...otherGuidePages] = guidePages;

  return (
    <GuidePageContainer>
      <GuidePageColumn>
        <IndexPageTitle>{I18n.t("guides.title")}</IndexPageTitle>
        <GuidePagePreview guidePage={firstGuidePage} />
        <GuidePagesPreviewContainer>
          {otherGuidePages.map((guidePage) => (
            <GuidePagePreview
              key={guidePage.attributes.slug}
              guidePage={guidePage}
            />
          ))}
        </GuidePagesPreviewContainer>
      </GuidePageColumn>
    </GuidePageContainer>
  );
};

const GuidePageContainer = styled.div`
  margin-top: ${AppHeaderHeight};
  flex-grow: 1;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GuidePageColumn = styled.div`
  font-family: adobe-clean, sans-serif;
  color: var(--spectrum-gray-900);
  text-align: left;
  ${imageWidth}
  margin-bottom: 112px;

  a,
  a:visited {
    color: var(--spectrum-indigo-900);
  }
`;
